import React, { memo, useCallback, useEffect, useState } from "react";
import { EquipmentDocumentI } from "../../../../libs/interfaces";
import { Image, Button as ButtonAntd } from "antd";
import {
  ClearOutlined,
  FilePdfOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Grid, Paper, TextField, Typography, Box } from "@mui/material";
import { comprimirImagen, Previews, FileI } from "../../../../libs/helpers";

export const arrDocumentsEmpty: EquipmentDocumentI[] = [
  {
    order: 1,
    src: "",
    observation: "",
    uuid: "",
    fileName: "",
    fileType: "",
  },
  {
    order: 2,
    src: "",
    observation: "",
    uuid: "",
    fileName: "",
    fileType: "",
  },
  {
    order: 3,
    src: "",
    observation: "",
    uuid: "",
    fileName: "",
    fileType: "",
  },
];

export interface DocumentComponentI {
  data: EquipmentDocumentI;
  onUpdate: (data: EquipmentDocumentI, uuid: string) => void;
}

export const DocumentComponent = memo(
  ({ data, onUpdate }: DocumentComponentI) => {
    const [laData, setData] = useState<EquipmentDocumentI>(
      {} as EquipmentDocumentI
    );
    const [files, setFiles] = useState<FileI[]>([]);
    const [continuoDoc, setContinuoDoc] = useState(false);

    useEffect(() => {
      setData(data);
    }, [data]);

    useEffect(() => {
      if (continuoDoc) {
        setContinuoDoc(false);
        handleProcessDoc();
      }
    }, [continuoDoc]);

    const handleObservationBlur = (e: any) => {
      onUpdate({ ...laData, observation: e.target.value }, data.uuid);
    };

    const handleFile = useCallback(
      (acceptedFiles: any) => {
        if (acceptedFiles.length === 0) {
          setData((elData) => ({
            ...elData,
            src: "",
            fileName: "",
            fileType: "",
            fileSize: 0,
          }));
          onUpdate(
            {
              ...laData,
              src: "",
              fileName: "",
              fileType: "",
              fileSize: 0,
            },
            data.uuid
          );
          return;
        }
        setFiles(
          acceptedFiles.map((file: any) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          )
        );
        setContinuoDoc(true);
      },
      [laData, data.uuid, onUpdate]
    );

    const handleProcessDoc = async () => {
      if (files.length === 0) {
        setData((elData) => ({
          ...elData,
          src: "",
          fileName: "",
          fileType: "",
          fileSize: 0,
        }));
        onUpdate(
          {
            ...laData,
            src: "",
            fileName: "",
            fileType: "",
            fileSize: 0,
          },
          data.uuid
        );
      } else {
        const file = files[0] as unknown as File;
        const fileType = file.type;
        const fileName = file.name;
        const fileSize = file.size;

        // Si es imagen, comprimir
        if (fileType.startsWith("image/")) {
          const imageComp = await comprimirImagen(file as unknown as Blob, 70);
          const reader = new FileReader();

          reader.readAsDataURL(imageComp as unknown as Blob);

          reader.onloadend = () => {
            const newData = {
              ...laData,
              src: reader.result as string,
              fileName,
              fileType: "image",
              fileSize,
            };
            setData(newData);
            onUpdate(newData, data.uuid);
          };
        }
        // Si es PDF, convertir a base64
        else if (fileType === "application/pdf") {
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onloadend = () => {
            const newData = {
              ...laData,
              src: reader.result as string,
              fileName,
              fileType: "pdf",
              fileSize,
            };
            setData(newData);
            onUpdate(newData, data.uuid);
          };
        }
      }
    };

    const formatFileSize = (bytes: number = 0) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    const handleClear = () => {
      const clearedData = {
        ...laData,
        src: "",
        fileName: "",
        fileType: "",
        fileSize: 0,
      };
      setData(clearedData);
      onUpdate(clearedData, data.uuid);
      setFiles([]);
    };

    const handleDownload = () => {
      if (laData.src) {
        const link = document.createElement("a");
        link.href = laData.src;
        link.download = laData.fileName || "documento";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    if (laData.src) {
      return (
        <Paper
          elevation={2}
          sx={{
            m: 0.5,
            p: 1,
            minWidth: "100%",
            height: 165,
            maxHeight: 165,
            minHeight: 165,
          }}
        >
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs={5}>
              {laData.fileType === "image" ? (
                <Image
                  width="100%"
                  height={130}
                  src={data.src}
                  style={{ borderRadius: 5 }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: 130,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: "#eeeeee",
                      transform: "scale(1.02)",
                    },
                  }}
                  onClick={handleDownload}
                >
                  <FilePdfOutlined
                    style={{ fontSize: 48, color: "#d32f2f" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      textAlign: "center",
                      px: 1,
                      wordBreak: "break-word",
                      maxWidth: "100%",
                    }}
                  >
                    {laData.fileName}
                  </Typography>
                  {laData.fileSize && (
                    <Typography variant="caption" color="textSecondary">
                      {formatFileSize(laData.fileSize)}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
            <Grid item xs={7}>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <ButtonAntd
                      type="dashed"
                      danger
                      icon={<ClearOutlined />}
                      size="small"
                      onClick={handleClear}
                      style={{ marginRight: 8 }}
                    >
                      Limpiar
                    </ButtonAntd>
                    {laData.src && (
                      <ButtonAntd
                        type="primary"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                      >
                        Descargar
                      </ButtonAntd>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      id="observation"
                      name="observation"
                      color="info"
                      multiline
                      rows={3}
                      label="Detalle"
                      value={laData.observation || ""}
                      onChange={(e) => {
                        setData((prevData) => ({
                          ...prevData,
                          observation: e.target.value,
                        }));
                      }}
                      onBlur={handleObservationBlur}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={2}
        sx={{
          m: 0.5,
          p: 1,
          minWidth: "100%",
          height: 165,
          maxHeight: 165,
          minHeight: 165,
        }}
      >
        <Grid container spacing={0.5}>
          <Grid item xs={5}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Previews
                handleFile={handleFile}
                files={files}
                maxFiles={1}
                typeFile="image/*,application/pdf"
                nameElement="Documento"
                noShow={true}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "0.7rem",
                }}
              >
                PDF o Imagen
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={7}>
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    id="observation"
                    name="observation"
                    color="info"
                    multiline
                    rows={4}
                    label="Detalle"
                    value={laData.observation || ""}
                    onChange={(e) => {
                      setData((prevData) => ({
                        ...prevData,
                        observation: e.target.value,
                      }));
                    }}
                    onBlur={handleObservationBlur}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    );
  }
);